import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CONVERTED } from "../lib/gtag";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "mt-2 w-full rounded-sm border border-white/12 bg-obsidian-800 px-4 py-3 text-white placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors";

export default function AssessmentForm({ testid = "assessment-form", source = null }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ contact_name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) return toast.error("Please add your full name.");
    if (!form.phone.trim()) return toast.error("Please add your mobile number.");
    if (!form.email.trim()) return toast.error("Please add your email address.");
    setSending(true);
    try {
      await api.post("/leads", {
        type: "assessment",
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        data: source ? { source_article: source } : {},
      });
      toast.success("Sent — the Speedy team will be in touch.");
      nav("/thank-you", CONVERTED("assessment"));
    } catch {
      toast.error("Something went wrong. Please try again or call 0466 495 495.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={testid}
          className="rounded-lg border border-gold/25 bg-gradient-to-b from-obsidian-800 to-obsidian p-7 md:p-10">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Obligation-free proposal</span>
      <h3 className="mt-3 font-heading font-black text-2xl md:text-3xl tracking-tight">
        SECURE YOUR CUSTOM TRANSFORMATION.
      </h3>
      <p className="mt-3 max-w-2xl text-sm md:text-base text-zinc-300">
        Three details — that's it. Our team will call you to arrange a site assessment and come back with an itemised proposal, no obligation.
      </p>

      <div className="mt-8 grid gap-6 md:grid-cols-3">
        <Field label="Full name">
          <input value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" autoComplete="name" required className={inputCls} data-testid={`${testid}-name`} />
        </Field>
        <Field label="Mobile number">
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder="04.." autoComplete="tel" inputMode="tel" required className={inputCls} data-testid={`${testid}-phone`} />
        </Field>
        <Field label="Email address">
          <input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" autoComplete="email" inputMode="email" required className={inputCls} data-testid={`${testid}-email`} />
        </Field>
      </div>

      <button type="submit" disabled={sending} data-testid={`${testid}-submit`}
              className="group mt-9 inline-flex w-full items-center justify-between gap-3 rounded-sm bg-gold px-8 py-5 font-heading font-black text-lg text-obsidian shadow-gold hover:bg-gold-hover disabled:opacity-50 transition-colors md:w-auto">
        {sending ? "Sending…" : "Request My Site Assessment"}
        <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
      </button>
      <p className="mt-4 text-xs text-zinc-500" data-testid={`${testid}-privacy-note`}>
        By submitting you agree to our <Link to="/privacy-policy" className="text-gold/80 hover:text-gold underline-offset-4 hover:underline">Privacy Policy</Link>. We only use your details to respond to this enquiry.
      </p>
      <p className="mt-4 font-mono text-[10px] text-zinc-600">
        Or call 0466 495 495 · info@speedyresurfacing.com.au
      </p>
    </form>
  );
}
